import React, { useState } from 'react';
import { Checkbox, Button, Space, List, Typography, Tag } from 'antd';

const { Text } = Typography;

export interface PortalLineItem {
  id: number;
  description: string;
  labor_hours: number;
  total_price: number;
  // Backend serves the raw SQLite column, so this arrives as the integer 1/0
  // (NOT a JS boolean). Type it loosely and normalize via `isLineTaxable`.
  is_taxable: boolean | number | string | null;
  approval_status: string;
  urgency?: { id: number; label: string; color: string } | null;
}

export type ApprovalAction = 'approve_selected' | 'approve_all' | 'decline_all';

const round2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

/**
 * Normalize the per-line taxability flag. The backend stores `is_taxable` as a
 * SQLite BOOLEAN (NOT NULL DEFAULT 1) and serves it as the integer 1/0, so the
 * naive `is_taxable !== false` test mis-taxes non-taxable lines (0 !== false is
 * true). Treat 0, "0", false, null and undefined as NON-taxable; everything
 * else (1, true) as taxable.
 */
export function isLineTaxable(is_taxable: PortalLineItem['is_taxable']): boolean {
  if (is_taxable === false || is_taxable === null || is_taxable === undefined) return false;
  if (is_taxable === 0 || is_taxable === '0') return false;
  return true;
}

/** Whole-job (header) discount — mirrors backend calculations.ts Discount. */
export interface PortalWholeJobDiscount {
  type: 'percentage' | 'amount' | 'final_price';
  value: number;
}

/** Fee amounts split by taxability — mirrors backend calculateDocumentTotal's fees arg. */
export interface PortalFees {
  taxable: number;
  nonTaxable: number;
}

/**
 * Sum the estimate's raw fee snapshot rows (as served by GET /portal/estimate/:token)
 * into a taxable / non-taxable split. Uses the stored `computed_amount` snapshot,
 * falling back to `value` for a flat fee that has no snapshot yet — identical to
 * the approval-PDF fee reader so the portal, PDF and bill agree.
 */
export function sumPortalFees(fees: any[]): PortalFees {
  const split: PortalFees = { taxable: 0, nonTaxable: 0 };
  for (const f of fees || []) {
    const amount = Number(f?.computed_amount ?? (f?.calculation_method === 'flat' ? f?.value : 0)) || 0;
    if (f?.is_taxable) split.taxable += amount;
    else split.nonTaxable += amount;
  }
  split.taxable = round2(split.taxable);
  split.nonTaxable = round2(split.nonTaxable);
  return split;
}

/**
 * Customer-facing approval totals. Tax applies to the taxable portion only
 * (a line is taxable unless explicitly is_taxable === false). When `selectedIds`
 * is provided, only those line items are included — used for the live "Selected"
 * running total as the customer ticks services.
 *
 * A whole-job (header) discount, when present, is applied to the included
 * subtotal AFTER line prices and BEFORE tax, and is spread proportionally across
 * the taxable portion so a partial selection still discounts fairly. Without
 * this, the customer was shown a total HIGHER than what the shop actually bills.
 */
export function computePortalApprovalTotals(
  lineItems: PortalLineItem[],
  taxRate: number,
  selectedIds?: Set<number>,
  wholeJobDiscount?: PortalWholeJobDiscount | null,
  fees?: PortalFees | null
): { subtotal: number; jobDiscount: number; fees: number; tax: number; total: number } {
  const items = selectedIds ? lineItems.filter((li) => selectedIds.has(li.id)) : lineItems;
  let subtotal = 0;
  let taxable = 0;
  for (const li of items) {
    const price = li.total_price ?? 0;
    subtotal += price;
    if (isLineTaxable(li.is_taxable)) taxable += price;
  }
  subtotal = round2(subtotal);
  taxable = round2(taxable);

  // Whole-job discount on the included subtotal (after line prices, before tax).
  let jobDiscount = 0;
  if (wholeJobDiscount && wholeJobDiscount.value != null && subtotal > 0) {
    switch (wholeJobDiscount.type) {
      case 'percentage':
        jobDiscount = (subtotal * wholeJobDiscount.value) / 100;
        break;
      case 'amount':
        jobDiscount = wholeJobDiscount.value;
        break;
      case 'final_price':
        jobDiscount = subtotal - wholeJobDiscount.value;
        break;
    }
    jobDiscount = Math.max(0, Math.min(jobDiscount, subtotal));
  }
  jobDiscount = round2(jobDiscount);

  // Fees are document-level: taxable fees join the tax base and all fees add to the
  // grand total — the same as backend calculateDocumentTotal. They apply in full
  // regardless of which lines the customer selects (the WO copies them wholesale).
  const taxableFees = round2(fees?.taxable ?? 0);
  const nonTaxableFees = round2(fees?.nonTaxable ?? 0);
  const feesTotal = round2(taxableFees + nonTaxableFees);

  // Spread the discount proportionally over the taxable share so tax is charged
  // on the discounted taxable base only.
  const taxableShare = subtotal > 0 ? taxable / subtotal : 0;
  const taxableAfterDiscount = round2(Math.max(0, taxable - jobDiscount * taxableShare));
  const tax = round2(((taxableAfterDiscount + taxableFees) * (taxRate || 0)) / 100);
  const total = round2(subtotal - jobDiscount + feesTotal + tax);
  return { subtotal, jobDiscount, fees: feesTotal, tax, total };
}

export interface PortalLineItemApprovalProps {
  lineItems: PortalLineItem[];
  onApprove: (input: { action: ApprovalAction; line_item_ids?: number[] }) => void;
  disabled?: boolean;
  /** Tax rate as a percentage (e.g. 7 for 7%). Sourced from estimate.tax_rate. */
  taxRate?: number;
  /** Whole-job (header) discount from the estimate, if any. */
  wholeJobDiscount?: PortalWholeJobDiscount | null;
  /** Document-level fees split by taxability, so the signed total matches the bill. */
  fees?: PortalFees | null;
}

const PortalLineItemApproval: React.FC<PortalLineItemApprovalProps> = ({ lineItems, onApprove, disabled, taxRate = 0, wholeJobDiscount, fees }) => {
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());

  const toggle = (id: number) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasSelection = checkedIds.size > 0;
  const selected = computePortalApprovalTotals(lineItems, taxRate, checkedIds, wholeJobDiscount, fees);
  const grand = computePortalApprovalTotals(lineItems, taxRate, undefined, wholeJobDiscount, fees);
  // Show the running total for what the customer has ticked; otherwise the full estimate.
  const shown = hasSelection ? selected : grand;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <List
        bordered
        dataSource={lineItems}
        renderItem={(item) => (
          <List.Item>
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Checkbox
                  checked={checkedIds.has(item.id)}
                  onChange={() => toggle(item.id)}
                  disabled={disabled || item.approval_status !== 'pending'}
                >
                  {item.description}
                  {item.urgency && (
                    <Tag color={item.urgency.color} style={{ marginLeft: 8 }}>{item.urgency.label}</Tag>
                  )}
                </Checkbox>
                <Text strong>${(item.total_price ?? 0).toFixed(2)}</Text>
              </Space>
              <Text type="secondary" style={{ fontSize: 12, paddingLeft: 24 }}>
                Labor {item.labor_hours}h
                {item.approval_status !== 'pending' && ` · ${item.approval_status}`}
              </Text>
            </Space>
          </List.Item>
        )}
      />
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text type="secondary">{hasSelection ? 'Selected subtotal' : 'Subtotal'}</Text>
          <Text>${shown.subtotal.toFixed(2)}</Text>
        </Space>
        {shown.jobDiscount > 0 && (
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Text type="secondary" style={{ color: '#ef4444' }}>Discount</Text>
            <Text style={{ color: '#ef4444' }}>-${shown.jobDiscount.toFixed(2)}</Text>
          </Space>
        )}
        {shown.fees > 0 && (
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Text type="secondary">Fees</Text>
            <Text>${shown.fees.toFixed(2)}</Text>
          </Space>
        )}
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text type="secondary">Tax{taxRate ? ` (${taxRate}%)` : ''}</Text>
          <Text>${shown.tax.toFixed(2)}</Text>
        </Space>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text strong>{hasSelection ? 'Selected total' : 'Total'}</Text>
          <Text strong>${shown.total.toFixed(2)}</Text>
        </Space>
      </Space>
      <Space wrap style={{ width: '100%' }}>
        <Button
          type="primary"
          disabled={disabled || checkedIds.size === 0}
          onClick={() => onApprove({ action: 'approve_selected', line_item_ids: Array.from(checkedIds).sort((a, b) => a - b) })}
        >
          Approve Selected
        </Button>
        <Button disabled={disabled} onClick={() => onApprove({ action: 'approve_all' })}>
          Approve All
        </Button>
        <Button danger disabled={disabled} onClick={() => onApprove({ action: 'decline_all' })}>
          Decline All
        </Button>
      </Space>
    </Space>
  );
};

export default PortalLineItemApproval;
